import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom"; // For redirecting to the sign-in page
import { formatDistanceToNow, parseISO } from "date-fns";
const apiUrl = process.env.REACT_APP_API_URL || "/api";

const Comments = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState(""); // Store user's full name
  const [userId, setUserId] = useState(""); // Store user's full name
  const navigate = useNavigate();

  useEffect(() => {
    // Check for authentication and retrieve user details
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user")); // Make sure user info is parsed correctly
    if (token && user) {
      const fullName = `${user.firstName} ${user.lastName}`;
      setUserName(fullName); // Set user's full name correctly parsed from user object
      setUserId(user.id);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }

    const fetchComments = async () => {
      try {
        const response = await axios.get(`${apiUrl}/comments/post/${postId}/`);
        setComments(
          response.data.map((comment) => ({ ...comment, approved: true }))
        ); // Assuming fetched comments are approved
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    };

    fetchComments();
  }, [postId]);

  const handleCommentChange = (event) => {
    setNewComment(event.target.value);
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();
    if (!userName) {
      alert("User name is not set. Please log in again.");
      return;
    }
    try {
      const response = await axios.post(`${apiUrl}/comments`, {
        author: userName, // Use the current logged-in user's name
        text: newComment,
        post_id: postId,
        user_id: userId,
      });
      // Assuming the response includes the comment just posted
      // Add it to the list with an 'approved' property set to false
      setComments([
        ...comments,
        { ...response.data, author: userName, approved: false },
      ]);
      setNewComment("");
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleLogin = () => {
    navigate("/sign-in"); // Redirect to sign-in page
  };

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h5" component="h3" gutterBottom>
        Comments
      </Typography>
      <List>
        {comments.map((comment) => (
          <ListItem
            key={comment.id}
            alignItems="flex-start"
            sx={{
              display: "flex", // Ensures flex properties are used
              flexDirection: "column", // Stacks the content vertically
              alignItems:
                comment.user_id === userId ? "flex-end" : "flex-start", // Aligns the box container
              backgroundColor:
                comment.user_id === userId ? "#f4f4f4" : "transparent",
              border: comment.user_id === userId ? "1px solid #ddd" : "none",
              borderRadius: 2,
              width: "100%", // Ensure the ListItem takes full width
            }}
          >
            <Box
              sx={{
                textAlign: comment.user_id === userId ? "right" : "left", // Text alignment inside the box
                width: "100%", // Ensures the text occupies the full width of the ListItem
              }}
            >
              <ListItemText
                primary={comment.text}
                secondary={
                  `${
                    comment.approved
                      ? `By ${comment.author} ${formatDistanceToNow(
                          parseISO(comment.created_at)
                        )} ago`
                      : ""
                  }` +
                  `${
                    !comment.approved
                      ? " - Your comment will be published once admin approves"
                      : ""
                  }`
                }
              />
            </Box>
          </ListItem>
        ))}
      </List>

      {isAuthenticated ? (
        <Box component="form" onSubmit={handleCommentSubmit} sx={{ mt: 2 }}>
          <TextField
            label="Add a comment"
            variant="outlined"
            fullWidth
            value={newComment}
            onChange={handleCommentChange}
            multiline
            rows={4}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 2, backgroundColor: "black" }}
          >
            Submit
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          sx={{ mt: 2, backgroundColor: "black" }}
          onClick={handleLogin}
        >
          Login to Comment
        </Button>
      )}
    </Box>
  );
};

export default Comments;
